import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/* ─────────────────────────────────────────────────────────────────────────
   L17.09 — Master Version History (`/admin/master-config/versions`, §35 #13).
   Generic version-history lookup over
   GET `v1/m17/governance/versions/:entityType/:entityId`. The types offered
   are those with history in the tenant (`versions/entity-types`) and a record
   is picked by code and name (`versions/records`) — never typed as a uuid (G18).
   ───────────────────────────────────────────────────────────────────────── */

/** A t215 row as the server sends it, with the changer named (G18). */
interface VersionRow {
  readonly versionId: string;
  readonly versionNumber: number;
  readonly changedAt: string;
  readonly changeReason: string | null;
  readonly actorName: string | null;
  readonly actorCode: string | null;
}

interface VersionedRecord {
  readonly entityId: string;
  readonly code: string | null;
  readonly name: string | null;
}

export function M17VersionHistoryPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [entityType, setEntityType] = useState('');
  const [entityId, setEntityId] = useState('');
  const [rows, setRows] = useState<VersionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: string[] }>('/v1/m17/governance/versions/entity-types', { token })
      .then((r) => { if (alive) setEntityTypes(r.data); })
      .catch((err) => { if (alive) setError((err as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function onLookup(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setRows(null);
    if (!entityType) { setError(t('m17.versionHistory.errEntityType')); return; }
    if (!entityId) { setError(t('m17.versionHistory.errEntityId')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: VersionRow[] }>(
        `/v1/m17/governance/versions/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
        { token },
      );
      setRows(r.data ?? []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="version-history">
      <h1>{t('m17.versionHistory.title')}</h1>
      <form onSubmit={onLookup} aria-label="version-lookup-form" noValidate>
        <label htmlFor="vh-entity">{t('m17.versionHistory.entityType')}</label>
        <select id="vh-entity" value={entityType} onChange={(e) => { setEntityType(e.target.value); setEntityId(''); }}>
          <option value="">{t('m17.versionHistory.pickType')}</option>
          {entityTypes.map((et) => <option key={et} value={et}>{et}</option>)}
        </select>
        <RecordPicker<VersionedRecord>
          endpoint={entityType ? `/v1/m17/governance/versions/records?entityType=${encodeURIComponent(entityType)}` : ''}
          idField="entityId"
          optionLabel={(r) => [r.code, r.name].filter(Boolean).join(' — ') || '—'}
          value={entityId}
          onPick={(id) => setEntityId(id)}
          label={t('m17.versionHistory.entityId')}
          testId="vh-id"
        />
        <button type="submit" disabled={busy}>{t('m17.versionHistory.lookupBtn')}</button>
      </form>

      {error ? <p role="alert">{error}</p> : null}

      {rows ? (
        rows.length === 0 ? <p>{t('m17.versionHistory.noHistory')}</p> : (
          <table aria-label="version-history-table">
            <thead><tr><th>{t('m17.versionHistory.colVersion')}</th><th>{t('m17.versionHistory.colBy')}</th><th>{t('m17.versionHistory.colAt')}</th><th>{t('m17.versionHistory.colRemarks')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.versionId}>
                  <td>{r.versionNumber}</td>
                  <td>{personLabel(r.actorName, r.actorCode) ?? '—'}</td>
                  <td>{at(r.changedAt)}</td>
                  <td>{r.changeReason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}
    </main>
  );
}
